export const CUSTOMER_ACCOUNT_KINDS = ["PERSON", "LEGAL_ENTITY", "HOUSEHOLD"] as const;
export type CustomerAccountKind = (typeof CUSTOMER_ACCOUNT_KINDS)[number];

/**
 * Lifecycle fields persisted on a Customer 360 account. They deliberately do
 * not derive from one another: an account can be registered before it has a
 * contract, access, or an activated benefit.
 */
export const CUSTOMER_ORIGINS = ["PREEXISTING", "NEW_ACQUISITION"] as const;
export type CustomerOrigin = (typeof CUSTOMER_ORIGINS)[number];

export const CUSTOMER_REGISTRATION_STATUSES = [
  "REGISTERED",
  "PENDING_REVIEW",
  "BLOCKED",
  "ARCHIVED",
] as const;
export type CustomerRegistrationStatus = (typeof CUSTOMER_REGISTRATION_STATUSES)[number];

export const CUSTOMER_CONTRACT_STATUSES = [
  "NOT_CONTRACTED",
  "PENDING_SIGNATURE",
  "ACTIVE",
  "SUSPENDED",
  "TERMINATED",
] as const;
export type CustomerContractStatus = (typeof CUSTOMER_CONTRACT_STATUSES)[number];

export const CUSTOMER_ACCESS_STATUSES = [
  "NOT_STARTED",
  "PENDING",
  "ACTIVE",
  "BLOCKED",
  "REVOKED",
] as const;
export type CustomerAccessStatus = (typeof CUSTOMER_ACCESS_STATUSES)[number];

export const CUSTOMER_BENEFIT_STATUSES = [
  "NOT_ACTIVATED",
  "PENDING_ELIGIBILITY",
  "ELIGIBLE",
  "ACTIVE",
  "SUSPENDED",
  "INELIGIBLE",
] as const;
export type CustomerBenefitStatus = (typeof CUSTOMER_BENEFIT_STATUSES)[number];

export const CUSTOMER_RELATIONSHIP_TYPES = [
  "FAMILY_MEMBER",
  "LEGAL_REPRESENTATIVE",
  "FINANCIAL_RESPONSIBLE",
  "GUARANTOR",
  "DRIVER",
  "EMPLOYER",
  "PARTNER",
  "OTHER",
] as const;
export type CustomerRelationshipType = (typeof CUSTOMER_RELATIONSHIP_TYPES)[number];

export interface CustomerIdentityCandidate {
  id: string;
  kind: CustomerAccountKind;
  displayName: string;
  identifierHash?: string;
  normalizedPhoneHash?: string;
  normalizedEmailHash?: string;
  birthDate?: string;
  postalCode?: string;
}

export interface DuplicateScore {
  leftId: string;
  rightId: string;
  score: number;
  reasons: string[];
  recommendation: "AUTO_LINK" | "MANUAL_REVIEW" | "IGNORE";
}

export function scoreDuplicateCandidates(
  left: CustomerIdentityCandidate,
  right: CustomerIdentityCandidate,
): DuplicateScore {
  if (left.id === right.id) throw new Error("Cannot compare a customer with itself.");
  let score = 0;
  const reasons: string[] = [];
  if (left.kind !== right.kind) {
    return {
      leftId: left.id,
      rightId: right.id,
      score: 0,
      reasons: ["different_kind"],
      recommendation: "IGNORE",
    };
  }
  if (left.identifierHash && left.identifierHash === right.identifierHash) {
    score += 100;
    reasons.push("same_identifier");
  }
  if (left.normalizedPhoneHash && left.normalizedPhoneHash === right.normalizedPhoneHash) {
    score += 25;
    reasons.push("same_phone");
  }
  if (left.normalizedEmailHash && left.normalizedEmailHash === right.normalizedEmailHash) {
    score += 25;
    reasons.push("same_email");
  }
  if (normalizeName(left.displayName) === normalizeName(right.displayName)) {
    score += 20;
    reasons.push("same_name");
  }
  if (left.birthDate && left.birthDate === right.birthDate) {
    score += 20;
    reasons.push("same_birth_date");
  }
  if (left.postalCode && left.postalCode === right.postalCode) {
    score += 5;
    reasons.push("same_postal_code");
  }
  return {
    leftId: left.id,
    rightId: right.id,
    score,
    reasons,
    recommendation: score >= 100 ? "AUTO_LINK" : score >= 45 ? "MANUAL_REVIEW" : "IGNORE",
  };
}

export function normalizeName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

export interface CustomerTimelineEvent {
  id: string;
  eventType: string;
  occurredAt: string;
  title: string;
  description?: string;
  source: string;
  actorName?: string;
  metadata?: Record<string, unknown>;
}

export function sortCustomerTimeline(
  events: readonly CustomerTimelineEvent[],
): CustomerTimelineEvent[] {
  return [...events].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}
